import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { ApiErrorResponse } from '../../common/types';
import { useErrorStore } from '../../stores/errorStore';

export interface UseErrorHandlerOptions {
  /** Context for error messages (e.g., 'loading users', 'saving profile') */
  context?: string;
}

/**
 * Simple hook for consistent error handling
 * Replaces repetitive setError/setLoading patterns
 */
export const useErrorHandler = (options: UseErrorHandlerOptions = {}) => {
  const { context = 'operation' } = options;

  const { addToast, setLoadingState, clearLoadingState, loadingStates } =
    useErrorStore();

  // Get loading state for this context
  const isLoading = loadingStates[context] || false;

  // Handle errors with consistent patterns
  const handleError = useCallback(
    (error: unknown, customMessage?: string): ApiErrorResponse => {
      let apiError: ApiErrorResponse;

      // Handle different error types
      if (error && typeof error === 'object' && 'success' in error) {
        // Already an ApiErrorResponse
        apiError = error as ApiErrorResponse;
      } else if (error instanceof AxiosError) {
        // Axios error - check for API response
        if (error.response?.data?.success === false) {
          apiError = error.response.data as ApiErrorResponse;
        } else {
          // Create network error from Axios error
          apiError = {
            success: false as const,
            error: {
              code: 'NETWORK_ERROR',
              message: customMessage || `Failed to ${context}`,
              details:
                error.message || 'Please check your connection and try again',
            },
            request_id: `client_${Date.now()}`,
            timestamp: new Date().toISOString(),
          };
        }
      } else {
        // Generic error
        apiError = {
          success: false as const,
          error: {
            code: 'NETWORK_ERROR',
            message: customMessage || `Failed to ${context}`,
            details:
              error instanceof Error
                ? error.message
                : 'Please check your connection and try again',
          },
          request_id: `client_${Date.now()}`,
          timestamp: new Date().toISOString(),
        };
      }

      // Show error notification
      addToast({
        type: 'error',
        message: apiError.error.message,
      });

      return apiError;
    },
    [context, addToast],
  );

  // Handle success messages
  const handleSuccess = useCallback(
    (message: string) => {
      addToast({
        type: 'success',
        message,
      });
    },
    [addToast],
  );

  // Set loading state
  const setLoading = useCallback(
    (loading: boolean) => {
      if (loading) {
        setLoadingState(context, true);
      } else {
        clearLoadingState(context);
      }
    },
    [context, setLoadingState, clearLoadingState],
  );

  return {
    handleError,
    handleSuccess,
    setLoading,
    isLoading,
  };
};
